import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

import { Routes } from "@ctypes";
import { useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { home } from "@routes/DashboardRoute/Home/tokens";
import { FutureMoneyLogo } from "./FutureMoneyLogo";

/**
 * The design-system navigation used on every main app screen. Pass
 * `fixed` where page content expects a fixed header (classic MainLayout
 * screens); the home screen renders it in flow.
 */

const Fixed = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: ${home.navBg};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  height: 76px;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 ${home.pagePad};
  border-bottom: 1px solid ${home.line08};
`;

const Wordmark = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    display: block;
  }
`;

const Tabs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Tab = styled.button<{ $active?: boolean }>`
  height: 36px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border: none;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? home.fill10 : "transparent")};
  font-family: ${home.sans};
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? home.ink : home.ink58)};
  cursor: pointer;
  transition: color 160ms ease;

  &:hover {
    color: ${home.ink};
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
`;

const ContributeButton = styled.button`
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 22px;
  border: none;
  border-radius: 999px;
  background: ${home.ink};
  color: ${home.ground};
  font-family: ${home.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 160ms ease;

  &:hover {
    filter: brightness(1.08);
  }
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: ${home.avatarCopperBg};
  border: 1px solid ${home.avatarCopperBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: ${home.copperText};
  cursor: pointer;
`;

const TABS: Array<{ route: Routes; label: keyof typeof Translations }> = [
  { route: Routes.Dashboard, label: "MainHeaderHomeTab" },
  { route: Routes.Accounts, label: "MainHeaderAccountsTab" },
  { route: Routes.Transfers, label: "MainHeaderTransfersTab" },
  { route: Routes.Profile, label: "MainHeaderProfileTab" },
];

export const AppNav = ({ fixed = false }: { fixed?: boolean }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useUser();

  const initial = (user?.firstName ?? "").charAt(0).toUpperCase();

  const bar = (
    <Bar>
      <Wordmark onClick={() => navigate(Routes.Dashboard)}>
        <FutureMoneyLogo />
      </Wordmark>

      <Tabs>
        {TABS.map(({ route, label }) => (
          <Tab key={route} $active={pathname === route} onClick={() => navigate(route)}>
            {i18n.t(Translations[label])}
          </Tab>
        ))}
      </Tabs>

      <Right>
        <ContributeButton onClick={() => navigate(Routes.OneTimeContribution)}>
          {i18n.t(Translations.HomeContribute)}
        </ContributeButton>
        {initial ? <Avatar onClick={() => navigate(Routes.Profile)}>{initial}</Avatar> : null}
      </Right>
    </Bar>
  );

  return fixed ? <Fixed>{bar}</Fixed> : bar;
};
